import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { FormikErrors, FormikValues } from 'formik';
import { api } from '@/modules/common/api/index.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { CURRENT_ADMIN_ISSUERS_QUERY_KEY } from '@/modules/admin-dashboard/queries/issuers.tsx';
import {
  GetAdminEntitiesListV1UsersAdminEntitiesGetParams,
  GetEntitiesResponse,
  UpdateEntityResponse,
  UpdateEntityStatusRequest,
} from 'api';

export const ADMIN_ENTITIES_LIST_QUERY_KEY = 'vestra/admin-entities-list';

export const useAdminEntitiesListQuery = (
  payload: GetAdminEntitiesListV1UsersAdminEntitiesGetParams,
  disabled = false,
) => {
  const { page } = payload;
  return useInfiniteQuery<GetEntitiesResponse>({
    queryKey: [ADMIN_ENTITIES_LIST_QUERY_KEY, JSON.stringify(payload)],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getAdminEntitiesListV1UsersAdminEntitiesGet({
        ...payload,
        page: pageParam || page,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useAdminUpdateEntityStatus = ({
  setErrors,
  onSuccess,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: () => void;
} = {}) => {
  return useMutation({
    mutationFn: async (payload: UpdateEntityStatusRequest) => {
      const response = await withErrorHandling<UpdateEntityResponse>(
        api.v1.updateEntityStatusV1UsersEntitiesStatusPatch(payload),
        {
          loading: 'Updating holder status...',
          success: 'Holder status updated',
          setErrors,
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([ADMIN_ENTITIES_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([CURRENT_ADMIN_ISSUERS_QUERY_KEY]);
      onSuccess && onSuccess();
    },
  });
};
